import React, {useCallback} from 'react';
import Box from '@mui/material/Box';
import {useParams} from 'react-router';
import {Concept} from 'types';
import type {HttpError} from 'api/http/types';
import type {Data, Part, Schema} from 'schemaDefinition/types';
import {isFeatureEnabled} from 'configuration';
import {useLocalization} from 'localization';
import {
  useGetAgentQuery,
  useMergeAgentsMutation,
} from 'services/agent/agentApi';
import {assertAgentValue} from 'services/utils';
import {mapRtkErrorToHttpError} from 'store/api';
import {Card, FlexBox, Scene, Text, useSnacks} from 'components';
import {RtkContent} from 'components/rtkContent';
import {DataFormCompareLayout} from 'schema/form/components/DataFormCompareLayout';
import {useSchema} from 'schemas';
import {AgentWorksTable} from 'widgets/agentWorks';
import {CompareEntities} from 'widgets/compareEntities';
import {useAgentFormConfiguration} from 'editors/agent/hooks/useAgentFormConfiguration';
import {useSceneExtensionsContext} from 'sceneExtensions';

export const MergeAgentsSceneCore: React.FC = () => {
  const {t} = useLocalization();
  const {successSnack, errorSnack} = useSnacks();
  const {currentExtension} = useSceneExtensionsContext();
  const configuration = useAgentFormConfiguration();
  const [mergeAgents] = useMergeAgentsMutation();

  const {targetId: targetIdParam, sourceId: sourceIdParam} = useParams();
  const targetId = targetIdParam || '';
  const sourceId = sourceIdParam || '';

  const {
    data: targetData,
    status: targetStatus,
    error: targetError,
  } = useGetAgentQuery({
    id: targetId,
  });
  const {
    data: sourceData,
    status: sourceStatus,
    error: sourceError,
  } = useGetAgentQuery({
    id: sourceId,
  });

  const completeSchema = useSchema({
    entity: Concept.agent,
    subType: targetData?.agentType ?? Concept.person,
  });

  const schema: Schema = {
    ...completeSchema,
    parts: completeSchema.parts.filter(
      p => (p as Part).name !== 'works' && (p as Part).name !== 'prizes',
    ),
  };
  const schemaAgentPrizes: Schema = {
    ...completeSchema,
    parts: completeSchema.parts.filter(p => (p as Part).name === 'prizes'),
  };

  const onSave = useCallback(
    (target: Data, source: Data) => {
      if (!isFeatureEnabled('agentMerge')) {
        errorSnack('Ikke aktivert/implementert ennå');
        return Promise.reject();
      }

      try {
        return mergeAgents({
          target: assertAgentValue(target),
          source: assertAgentValue(source),
        })
          .unwrap()
          .then(() => {
            successSnack('Agentene ble slått sammen');
            return Promise.resolve();
          })
          .catch(error => {
            errorSnack('En feil oppstod!', mapRtkErrorToHttpError(error));
            return Promise.reject();
          });
      } catch (e) {
        errorSnack('En uventet feil oppstod!', e as unknown as HttpError);
        return Promise.reject();
      }
    },
    [errorSnack, mergeAgents, successSnack],
  );

  return (
    <Scene
      browserTitle={t('appbar.mergeAgent')}
      rightPanelConfig={currentExtension?.panelConfig}>
      <Scene.Header title={t('appbar.mergeAgent')} />
      <Scene.Content>
        <RtkContent status={targetStatus} error={targetError}>
          <RtkContent status={sourceStatus} error={sourceError}>
            {targetData && sourceData ? (
              <CompareEntities
                compareEntitiesContextProps={{
                  targetData,
                  sourceData,
                  schema,
                  onSave,
                }}
                compareEntitiesFormProps={{
                  formConfiguration: configuration,
                  targetHeader: (
                    <Box
                      sx={{
                        ml: 2,
                      }}>
                      <Text
                        kind="primary"
                        variant="h4"
                        textTransform="uppercase">
                        Agent som beholdes
                      </Text>
                      <Text kind="primary" variant="caption">
                        Denne agenten vil få alle koblingene, og eventuelle
                        endringer som gjøres.
                      </Text>
                    </Box>
                  ),
                  sourceHeader: (
                    <Box
                      sx={{
                        ml: 2,
                      }}>
                      <Text
                        kind="primary"
                        variant="h4"
                        textTransform="uppercase">
                        Agent som slettes
                      </Text>
                      <Text kind="primary" variant="caption">
                        Koblingene vil bli flyttet fra denne agenten til den
                        andre agenten, og denne agenten vil bli slettet etterpå.
                      </Text>
                    </Box>
                  ),
                  additionalComparisons: [
                    {
                      target: {
                        schema: schemaAgentPrizes,
                        data: targetData!,
                      },
                      source: {
                        schema: schemaAgentPrizes,
                        data: sourceData!,
                      },
                    },
                  ],
                }}
              />
            ) : null}
          </RtkContent>
        </RtkContent>

        <FlexBox horizontal flex={1} mb={4}>
          <DataFormCompareLayout
            renderCurrent={() => (
              <Card level="level2">
                <AgentWorksTable agent={targetData} />
              </Card>
            )}
            renderProposed={() => (
              <Card level="level2">
                <AgentWorksTable agent={sourceData} />
              </Card>
            )}
          />
        </FlexBox>
      </Scene.Content>
      {currentExtension ? (
        <Scene.RightPanel>
          {currentExtension.renderRightPanel()}
        </Scene.RightPanel>
      ) : null}
    </Scene>
  );
};
